import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { IStudentEvalRow } from './JudgePortalTypes';
import './JudgePortal.css';

const StudentEvalsPage = () => {
  const [evals, setEvals] = useState<IStudentEvalRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    store.server
      .postApi<ServerResponse<{ evals: IStudentEvalRow[] }>>('../JudgePortal/StudentEvalsData', {})
      .then((x) => {
        if (x.Success && x.Value) {
          setEvals(x.Value.evals || []);
        } else {
          toast.error(x.Message || 'Unable to load evaluations.', { autoClose: false });
          setEvals(null);
        }
      })
      .catch(() => {
        toast.error('Unable to load evaluations.', { autoClose: false });
        setEvals(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    if (!evals?.length) return [] as { key: string; rows: IStudentEvalRow[] }[];
    const map = new Map<string, IStudentEvalRow[]>();
    evals.forEach((e) => {
      const key = `${e.projectDisplayId}-${e.projectId}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    return Array.from(map.entries()).map(([key, rows]) => ({ key, rows }));
  }, [evals]);

  if (loading) {
    return (
      <div className="main-body-page grow student-evals-page" data-testid="student-evals-page">
        <h1>Download Evaluations</h1>
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="main-body-page grow student-evals-page" data-testid="student-evals-page">
      <h1>Download Evaluations</h1>
      <div className="eval-container">
        {evals == null && (
          <div className="alert alert-warning">Evaluations are not available.</div>
        )}
        {evals != null && evals.length === 0 && (
          <div className="eval">
            <div>
              <h2>Sorry, We can&apos;t find any evaluations at this time.</h2>
              <div>If you feel this is a mistake, please check with your teacher.</div>
            </div>
          </div>
        )}
        {groups.map((g) => {
          const first = g.rows[0];
          const ordered = g.rows
            .slice()
            .sort(
              (a, b) =>
                Number(!!b.isFinalRound) - Number(!!a.isFinalRound) ||
                (b.name || '').localeCompare(a.name || '')
            );
          return (
            <div className="eval-group" key={g.key}>
              <div className="flex-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h2>{first.projectDisplayId}</h2>
                <h3>{first.title}</h3>
              </div>
              {ordered.map((evalRow, idx) => (
                <div className="eval" key={`${evalRow.roundId}-${evalRow.roomId}-${idx}`}>
                  <h2 style={{ margin: 0 }}>{evalRow.name}</h2>
                  <a
                    href={StoreX.BuildUrl(
                      `../JudgePortal/PrintEval?RoundId=${evalRow.roundId}&roomId=${evalRow.roomId}&entryId=${evalRow.projectId}`
                    )}
                    target="_blank"
                    rel="noreferrer"
                    title="download eval"
                  >
                    <i className="fa fa-file-text-o" /> Download
                  </a>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudentEvalsPage;
